import {
  type CMSFilterableTileContainerDTO,
  type CMSFilterableTileContainerFilterDTO,
  type CMSFilterableTileContainerTileDTO,
} from "~/features/cms/components/cmsFilterableTile/dto";
import { styled } from "@schaeffler-commerce/design-system";
import { C_ON_SURFACE } from "@schaeffler-commerce/design-system";
import { DesktopContainerTypes, MobileContainerTypes } from "~/features/cms/components/cmsTeaser/CMSTeasers";
import { Card } from "~/features/configurator/Linear/components/Card";
import { CMSMedia } from "~/features/cms/components/cmsMedia/CMSMedia";
import { cleanupText } from "~/features/cms/utils/markup";
import { resolveUrl } from "~/utils/url/urlResolver";
import { CMSTeaserContainer } from "~/features/cms/components/cmsTeaser/CMSTeaserContainer";
import { useTileFilter } from "~/features/cms/components/cmsFilterableTile/useTileFilter";
import { useIsMinDesktopSize } from "~/common/hooks/useMediaQuery";

interface Props extends Omit<CMSFilterableTileContainerDTO, "type"> {
  className?: string;
}

export type FilterData = CMSFilterableTileContainerFilterDTO;

// Migrated from CMSFilterableTileContainer.less (title margin rule).
const Title = styled("p")({ margin: 0 });

// Migrated from CMSFilterableTileContainer.less (__tile: flex column card).
const TileCard = styled(Card)({
  display: "flex",
  flexDirection: "column",
});

// Migrated from CMSFilterableTileContainer.less (__tile__content).
const TileContent = styled("div")({
  padding: "16px",
  fontWeight: "bold",
  color: C_ON_SURFACE,
});

export const CMSFilterableTileContainer = ({
  className,
  title,
  showAllLabel,
  filters = [],
  teasers = [],
  scrollType: desktopScrollType = DesktopContainerTypes.DEFAULT,
  itemsPerRow: desktopItemsPerRow = 1,
  mobileScrollType = MobileContainerTypes.DEFAULT,
  mobileItemsPerRow = 1,
}: Props) => {
  const isDesktop = useIsMinDesktopSize();
  const { filterContainer, visibleTiles } = useTileFilter({
    filters,
    showAllLabel,
    allTiles: teasers,
    precededByTitle: Boolean(title),
  });

  return (
    <div className={className}>
      {title && <Title className="headline_h3_left headline_h3_left--default">{title}</Title>}

      {filterContainer}

      <CMSTeaserContainer
        scrollType={desktopScrollType}
        itemsPerRow={desktopItemsPerRow}
        mobileScrollType={mobileScrollType}
        mobileItemsPerRow={mobileItemsPerRow}
        teasers={visibleTiles}
        renderTeaser={(t) => <DataTile {...t} isDesktop={isDesktop} />}
        renderTeaserDetailsContainer={() => null}
      />
    </div>
  );
};

interface DataTileProps extends Omit<CMSFilterableTileContainerTileDTO, "type" | "tags"> {
  className?: string;
  onClick?: () => void;
  isDesktop: boolean;
}

const DataTile = ({ className, onClick, media, label, isDesktop, url, target }: DataTileProps) => {
  const tag = url ? "a" : "div";
  label = cleanupText(label);

  return (
    <TileCard
      as={tag}
      disableHover={tag === "div"}
      className={className}
      onClick={onClick}
      href={url ? resolveUrl(url) : undefined}
      target={target === "newWindow" ? "_blank" : undefined}
    >
      {media && <CMSMedia {...media} ratio={"16/9"} />}
      {label && <TileContent className="copy_left">{label}</TileContent>}
    </TileCard>
  );
};
